export interface BlackScreenCheckResult {
    isBlack: boolean;
    blackCount: number;
    totalChecks: number;
    cancelled?: boolean;
}

export interface BlackScreenDetectorOptions {
    threshold?: number;
    sampleRate?: number;
}

let sharedCanvas: HTMLCanvasElement | null = null;
let sharedContext: CanvasRenderingContext2D | null = null;

const ANALYSIS_WIDTH = 64;
const ANALYSIS_HEIGHT = 36;

const FRAME_CHECK_DELAY_MS = 100;

function isBlackScreen(videoElement: HTMLVideoElement, options: BlackScreenDetectorOptions = {}): boolean | null {
    const threshold = options.threshold ?? 0.0001;
    const sampleRate = options.sampleRate ?? 2;

    if (!videoElement.videoWidth || !videoElement.videoHeight) {
        return null;
    }

    if (!sharedCanvas) {
        sharedCanvas = document.createElement('canvas');
        sharedCanvas.width = ANALYSIS_WIDTH;
        sharedCanvas.height = ANALYSIS_HEIGHT;
        sharedContext = sharedCanvas.getContext('2d', { willReadFrequently: true });
    }

    if (!sharedContext) {
        return false;
    }

    sharedContext.drawImage(videoElement, 0, 0, ANALYSIS_WIDTH, ANALYSIS_HEIGHT);
    const imageData = sharedContext.getImageData(0, 0, ANALYSIS_WIDTH, ANALYSIS_HEIGHT);
    const pixels = imageData.data;

    let totalBrightness = 0;
    let sampleCount = 0;

    for (let i = 0; i < pixels.length; i += 4 * sampleRate) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        const brightness = (r + g + b) / 3;

        totalBrightness += brightness;
        sampleCount++;
    }

    const averageBrightness = totalBrightness / sampleCount;

    return averageBrightness < threshold;
}

export function checkBlackScreen(
    videoElement: HTMLVideoElement,
    framesCount = 10
): { promise: Promise<BlackScreenCheckResult>; cancel: () => void } {
    let blackCount = 0;
    let checkedFrames = 0;
    let timeoutId: NodeJS.Timeout | null = null;
    let cancelled = false;
    let resolvePromise: ((value: BlackScreenCheckResult) => void) | null = null;

    const promise = new Promise<BlackScreenCheckResult>((resolve, reject) => {
        resolvePromise = resolve;

        const checkNextFrame = () => {
            if (cancelled) {
                resolve({
                    isBlack: false,
                    blackCount,
                    totalChecks: checkedFrames,
                    cancelled: true,
                });
                return;
            }

            if (!videoElement || framesCount <= 0) {
                reject(new Error('Invalid checkBlackScreen parameters'));
                return;
            }

            try {
                const isBlack = isBlackScreen(videoElement);

                if (isBlack !== null) {
                    if (isBlack) {
                        blackCount++;
                    }
                    checkedFrames++;

                    if (checkedFrames >= framesCount) {
                        const result = {
                            isBlack: blackCount === framesCount,
                            blackCount,
                            totalChecks: checkedFrames,
                        };

                        resolve(result);
                        return;
                    }
                }

                timeoutId = setTimeout(checkNextFrame, FRAME_CHECK_DELAY_MS);
            } catch (error) {
                reject(error);
            }
        };

        timeoutId = setTimeout(checkNextFrame, FRAME_CHECK_DELAY_MS);
    });

    const cancel = () => {
        cancelled = true;
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
        if (resolvePromise) {
            resolvePromise({
                isBlack: false,
                blackCount,
                totalChecks: checkedFrames,
                cancelled: true,
            });
            resolvePromise = null;
        }
    };

    return { promise, cancel };
}
